// Phase MHM-3 · Support Tickets — priority queue + detail pane.
import { useEffect, useState, useCallback } from "react";
import { Loader2, RefreshCw, Plus, X, Send, ArrowUp } from "lucide-react";
import { toast } from "sonner";
import api from "../../services/api";
import MHMSubNav from "../../components/hub/MHMSubNav";
import TrackingIdLink from "../../components/hub/TrackingIdLink";
import SlaAlertBadge from "../../components/hub/SlaAlertBadge";
import RoleGate from "../../components/RoleGate";

const PRIO_COLOR = {
  critical: "bg-rose-100 text-rose-800 border-rose-300",
  high:     "bg-orange-100 text-orange-800 border-orange-300",
  medium:   "bg-amber-100 text-amber-800 border-amber-300",
  low:      "bg-slate-100 text-slate-700 border-slate-300",
};

function NewTicketModal({ open, onClose, onCreated }) {
  const [data, setData] = useState({ priority: "medium", category: "other",
                                      subject: "", body: "", tracking_id: "" });
  if (!open) return null;
  const submit = async () => {
    if (!data.subject || !data.body) return toast.error("Subject + body required");
    try {
      const payload = { ...data };
      if (!payload.tracking_id) delete payload.tracking_id;
      await api.post("/hub/support/tickets", payload);
      toast.success("Ticket created"); onCreated(); onClose();
    } catch (e) { toast.error(e.userMessage || "Create failed"); }
  };
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="mhm-support-new-modal">
      <div className="bg-white rounded-2xl p-5 max-w-lg w-full">
        <div className="flex justify-between mb-3"><h3 className="font-semibold">New ticket</h3><button onClick={onClose}><X className="h-4 w-4" /></button></div>
        <div className="grid grid-cols-2 gap-3 text-sm">
          <select value={data.priority} onChange={(e) => setData({...data, priority: e.target.value})} className="border border-slate-200 rounded-lg px-2 py-1.5" data-testid="mhm-support-new-priority">
            {["critical", "high", "medium", "low"].map(p => <option key={p}>{p}</option>)}
          </select>
          <select value={data.category} onChange={(e) => setData({...data, category: e.target.value})} className="border border-slate-200 rounded-lg px-2 py-1.5" data-testid="mhm-support-new-category">
            {["lost_shipment", "damaged", "delay", "wrong_item", "refund_request", "agent_complaint", "other"].map(c => <option key={c}>{c}</option>)}
          </select>
          <input placeholder="Tracking ID (optional)" value={data.tracking_id} onChange={(e) => setData({...data, tracking_id: e.target.value})} className="border border-slate-200 rounded-lg px-2 py-1.5 col-span-2"/>
          <input placeholder="Subject" value={data.subject} onChange={(e) => setData({...data, subject: e.target.value})} className="border border-slate-200 rounded-lg px-2 py-1.5 col-span-2" data-testid="mhm-support-new-subject"/>
          <textarea placeholder="Description" value={data.body} onChange={(e) => setData({...data, body: e.target.value})} rows={3} className="border border-slate-200 rounded-lg px-2 py-1.5 col-span-2" data-testid="mhm-support-new-body"/>
        </div>
        <button onClick={submit} className="mt-3 w-full bg-violet-600 hover:bg-violet-700 text-white rounded-lg py-1.5 text-sm" data-testid="mhm-support-new-submit">Create</button>
      </div>
    </div>
  );
}

export default function SupportTickets() {
  const [tickets, setTickets] = useState([]);
  const [counts, setCounts] = useState({});
  const [activeId, setActiveId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [newOpen, setNewOpen] = useState(false);
  const [statusF, setStatusF] = useState("");
  const [priorityF, setPriorityF] = useState("");
  const [note, setNote] = useState("");
  const [reply, setReply] = useState("");

  const loadQueue = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusF) params.set("status", statusF);
      if (priorityF) params.set("priority", priorityF);
      const { data } = await api.get(`/hub/support/tickets?${params}`);
      setTickets(data.rows || []);
      setCounts(data.counts || {});
    } catch (e) { toast.error(e.userMessage || "Load failed"); }
    finally { setLoading(false); }
  }, [statusF, priorityF]);
  useEffect(() => { loadQueue(); }, [loadQueue]);

  const openDetail = async (t) => {
    setActiveId(t.ticket_id);
    try {
      const { data } = await api.get(`/hub/support/tickets/${t.ticket_id}`);
      setDetail(data);
    } catch (e) { toast.error("Detail load failed"); }
  };

  const escalate = async () => {
    const target = window.prompt("Escalate to user_id:");
    if (!target) return;
    const reason = window.prompt("Reason:");
    if (!reason) return;
    try {
      await api.post(`/hub/support/tickets/${activeId}/escalate`,
                     { escalated_to_user_id: target, reason });
      toast.success("Escalated"); openDetail({ ticket_id: activeId }); loadQueue();
    } catch (e) { toast.error(e.userMessage || "Failed"); }
  };
  const resolve = async () => {
    const summary = window.prompt("Resolution summary:");
    if (!summary) return;
    try {
      await api.post(`/hub/support/tickets/${activeId}/resolve`,
                     { resolution_summary: summary });
      toast.success("Resolved"); openDetail({ ticket_id: activeId }); loadQueue();
    } catch (e) { toast.error(e.userMessage || "Failed"); }
  };
  const addNote = async (internal) => {
    const body = internal ? note : reply;
    if (!body) return;
    try {
      await api.post(`/hub/support/tickets/${activeId}/notes`,
                     { body, internal });
      toast.success(internal ? "Note added" : "Reply sent");
      internal ? setNote("") : setReply("");
      openDetail({ ticket_id: activeId });
    } catch (e) { toast.error(e.userMessage || "Failed"); }
  };

  const grouped = { critical: [], high: [], medium: [], low: [] };
  tickets.forEach((t) => grouped[t.priority || "low"]?.push(t));

  return (
    <RoleGate allow={["main_hub_manager", "admin", "founder", "ceo", "micro_hub_lead"]}>
      <MHMSubNav />
      <div className="max-w-[1600px] mx-auto px-4 sm:px-6 py-6 space-y-4" data-testid="mhm-support-page">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 font-['Outfit']" data-testid="mhm-support-title">
              Support Tickets
            </h1>
            <p className="text-sm text-slate-500">Open: {counts.open || 0} · In progress: {counts.in_progress || 0} · Escalated: {counts.escalated || 0}</p>
          </div>
          <div className="flex items-center gap-2">
            <SlaAlertBadge />
            <button onClick={loadQueue} data-testid="mhm-support-refresh"
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm border border-slate-200 hover:bg-slate-50 rounded-lg">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />} Refresh
            </button>
            <button onClick={() => setNewOpen(true)} data-testid="mhm-support-new"
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm bg-violet-600 hover:bg-violet-700 text-white rounded-lg">
              <Plus className="h-4 w-4" /> New ticket
            </button>
          </div>
        </div>

        <div className="flex gap-2 flex-wrap">
          <select value={statusF} onChange={(e) => setStatusF(e.target.value)} className="text-xs border border-slate-200 rounded px-2 py-1" data-testid="mhm-support-status-filter">
            <option value="">All status</option>
            {["open", "in_progress", "escalated", "resolved", "closed"].map(s => <option key={s} value={s}>{s}</option>)}
          </select>
          <select value={priorityF} onChange={(e) => setPriorityF(e.target.value)} className="text-xs border border-slate-200 rounded px-2 py-1">
            <option value="">All priority</option>
            {["critical", "high", "medium", "low"].map(p => <option key={p} value={p}>{p}</option>)}
          </select>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Queue */}
          <div className="lg:col-span-1 space-y-2" data-testid="mhm-support-queue">
            {["critical", "high", "medium", "low"].map((p) => (
              <div key={p}>
                <div className="text-xs uppercase tracking-wider text-slate-500 mb-1 font-semibold">{p} · {grouped[p].length}</div>
                <div className="space-y-1">
                  {grouped[p].map((t) => (
                    <button key={t.ticket_id} onClick={() => openDetail(t)}
                            data-testid={`mhm-support-row-${t.ticket_id}`}
                            className={`text-left w-full border ${PRIO_COLOR[p]} rounded-lg p-2 hover:shadow ${activeId === t.ticket_id ? "ring-2 ring-violet-500" : ""}`}>
                      <div className="text-xs font-mono opacity-70">{t.ticket_id}</div>
                      <div className="text-sm font-medium truncate">{t.subject}</div>
                      <div className="text-[10px] uppercase tracking-wider mt-0.5">
                        {t.category} · {t.status}
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            ))}
            {tickets.length === 0 && !loading && (
              <div className="text-sm text-slate-400 py-8 text-center">No tickets</div>
            )}
          </div>

          {/* Detail */}
          <div className="lg:col-span-2" data-testid="mhm-support-detail">
            {!detail && <div className="bg-white border border-slate-200 rounded-2xl p-6 text-sm text-slate-400 text-center">Select a ticket to view detail</div>}
            {detail && (
              <div className="bg-white border border-slate-200 rounded-2xl p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-xs font-mono text-slate-500">{detail.ticket.ticket_id}</div>
                    {detail.ticket.tracking_id && (
                      <div className="text-xs text-slate-500 mt-0.5">Linked order: <TrackingIdLink tid={detail.ticket.tracking_id} /></div>
                    )}
                    <h3 className="text-lg font-semibold">{detail.ticket.subject}</h3>
                    <div className="text-xs text-slate-500 mt-1">
                      <span className={`px-2 py-0.5 rounded ${PRIO_COLOR[detail.ticket.priority]} border mr-2`}>{detail.ticket.priority}</span>
                      <span className="px-2 py-0.5 rounded bg-slate-100 border mr-2">{detail.ticket.category}</span>
                      <span className="px-2 py-0.5 rounded bg-blue-100 text-blue-800">{detail.ticket.status}</span>
                    </div>
                  </div>
                  <div className="flex gap-1">
                    <button onClick={escalate} className="text-xs px-2 py-1 border border-amber-200 hover:bg-amber-50 text-amber-700 rounded inline-flex items-center gap-1" data-testid="mhm-support-escalate">
                      <ArrowUp className="h-3 w-3" /> Escalate
                    </button>
                    <button onClick={resolve} className="text-xs px-2 py-1 bg-green-600 hover:bg-green-700 text-white rounded" data-testid="mhm-support-resolve">Resolve</button>
                  </div>
                </div>
                {detail.order && (
                  <div className="text-xs bg-slate-50 rounded-lg p-2">
                    <b>Order:</b> {detail.order.tracking_id} · {detail.order.status} · {detail.order.pickup_city} → {detail.order.drop_city}
                  </div>
                )}
                <div>
                  <div className="text-xs uppercase text-slate-500 mb-1">Internal notes ({detail.ticket.internal_notes.length})</div>
                  <div className="bg-slate-50 rounded-lg p-2 max-h-32 overflow-auto text-xs space-y-1">
                    {detail.ticket.internal_notes.length === 0 && <div className="text-slate-400">No internal notes</div>}
                    {detail.ticket.internal_notes.map((n, i) => <div key={i}>· {n.body} <span className="text-slate-400">({(n.created_at || "").slice(0, 16).replace("T", " ")})</span></div>)}
                  </div>
                  <div className="flex gap-1 mt-1">
                    <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Add internal note…" className="flex-1 text-xs border border-slate-200 rounded px-2 py-1" data-testid="mhm-support-note-input"/>
                    <button onClick={() => addNote(true)} className="text-xs px-2 py-1 bg-slate-700 text-white rounded" data-testid="mhm-support-note-add">Add</button>
                  </div>
                </div>
                <div>
                  <div className="text-xs uppercase text-slate-500 mb-1">Customer thread ({detail.ticket.customer_messages.length})</div>
                  <div className="bg-blue-50 rounded-lg p-2 max-h-32 overflow-auto text-xs space-y-1">
                    {detail.ticket.customer_messages.map((m, i) => <div key={i}>{m.direction === "in" ? "←" : "→"} {m.body}</div>)}
                  </div>
                  <div className="flex gap-1 mt-1">
                    <input value={reply} onChange={(e) => setReply(e.target.value)} placeholder="Reply to customer…" className="flex-1 text-xs border border-slate-200 rounded px-2 py-1"/>
                    <button onClick={() => addNote(false)} className="text-xs px-2 py-1 bg-violet-600 text-white rounded inline-flex items-center gap-1">
                      <Send className="h-3 w-3" /> Send
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      <NewTicketModal open={newOpen} onClose={() => setNewOpen(false)} onCreated={loadQueue}/>
    </RoleGate>
  );
}
